import React, { createContext, useContext, useState } from "react";
import { IDownloadData } from "../@types/Components";
import { IProviderProps } from "../@types/ContextProviderRelated";

/**
 * Interface for the ExportContext.
 *
 * @param {boolean} isExporting flag to indicate if the user is currently exporting data.
 * @param {(isExporting: boolean) => void} setIsExporting function to change the flag above.
 * @param {IDownloadData} downloadData object containing the exporting data.
 * @param {(newData: IDownloadData) => void} setDownloadData function to change the object above.
 * @param {number} imgPostProcessScale number indicating the scaling factor that will get applied in post processing of the image.
 * @param {(newScalingFactor: number) => void} setImgPostProcessScale function to change the value above.
 * @param {number} exportCameraHeight the height of the camera (zoom level) used during export, which can affect element scaling.
 * @param {number} exportCameraWidth the width of the camera used during export, which can affect element scaling.
 * @param {(dimensions: { camHeight: number, camWidth: number }) => void} setExportCameraDimensions function to update both the export camera height and width values.
 */
export interface IExportContext {
  isExporting: boolean;
  setIsExporting: (isExporting: boolean) => void;
  downloadData: IDownloadData | null;
  setDownloadData: (newData: IDownloadData) => void;
  imgPostProcessScale: number;
  setImgPostProcessScale: (newScalingFactor: number) => void;
  exportCameraHeight: number;
  exportCameraWidth: number;
  setExportCameraDimensions: (dimensions: {
    camHeight: number;
    camWidth: number;
  }) => void;
}

// Creating the default values for the export context.
const exportContextDefaultValues: IExportContext = {
  isExporting: false,
  setIsExporting: () => {},
  downloadData: null,
  setDownloadData: () => {},
  imgPostProcessScale: 0,
  setImgPostProcessScale: () => {},
  exportCameraHeight: 0,
  exportCameraWidth: 0,
  setExportCameraDimensions: () => {},
};

const ExportContext = createContext(exportContextDefaultValues);

// Creating a custom hook for easy access.
export const useExportContext = () => useContext(ExportContext);

/**
 * Creates a provider that contains the information, whether or not the user is currently exporting data.
 * Is needed to adjust the rendered data accordingly without messing up the scene afterwards.
 *
 * @param children the children elements to get wrapped inside the provider. They will all have access to the data.
 * @returns {ExportProvider} provider.
 */
const ExportProvider = ({ children }: IProviderProps): JSX.Element => {
  const [isExporting, setIsExporting] = useState<boolean>(
    exportContextDefaultValues.isExporting
  );
  const [downloadData, setDownloadData] = useState<IDownloadData | null>(
    exportContextDefaultValues.downloadData
  );
  const [imgPostProcessScale, setImgPostProcessScale] = useState<number>(
    exportContextDefaultValues.imgPostProcessScale
  );
  const [exportCameraHeight, setExportCameraHeight] = useState<number>(
    exportContextDefaultValues.exportCameraHeight
  );
  const [exportCameraWidth, setExportCameraWidth] = useState<number>(
    exportContextDefaultValues.exportCameraWidth
  );

  // Updated to accept an object with camHeight and camWidth
  const setExportCameraDimensions = ({
    camHeight,
    camWidth,
  }: {
    camHeight: number;
    camWidth: number;
  }) => {
    setExportCameraHeight(camHeight);
    setExportCameraWidth(camWidth);
  };

  return (
    <ExportContext.Provider
      value={{
        isExporting,
        setIsExporting,
        downloadData,
        setDownloadData,
        imgPostProcessScale,
        setImgPostProcessScale,
        exportCameraHeight,
        exportCameraWidth,
        setExportCameraDimensions,
      }}
    >
      {children}
    </ExportContext.Provider>
  );
};

export default ExportProvider;
